import { useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { callFunction } from '@/lib/api';
import { supabase } from '@/lib/supabase';
import { receiptTimesById } from '@/lib/referral/referralDateTime';
import type { ReceiptHistoryItem } from '@/lib/referral/referralReceipt';

export type CompanionPointer = {
  exchangeId: string;
  fileName: string;
};

export type OutboundReferralKind =
  | 'XREF01'
  | 'XREF02'
  | 'XREF06'
  | 'XDIS91'
  | 'XDIS13'
  | 'XDIS01'
  | 'XDIS02'
  | 'XDIS03'
  | 'XDIS05'
  | 'GGOP'
  | 'XDIS18'
  | 'XDIS21';

const OUTBOUND_KINDS: readonly OutboundReferralKind[] = [
  'XREF01',
  'XREF02',
  'XREF06',
  'XDIS91',
  'XDIS13',
  'XDIS01',
  'XDIS02',
  'XDIS03',
  'XDIS05',
  'GGOP',
  'XDIS18',
  'XDIS21',
];

/**
 * Block 2 (GGOP): a genoptræningsplan sits in the same outbox so receipts couple to it,
 * but it is shown only in the GGOP outbox. Every other list keeps showing exactly the
 * referral and DIS kinds it showed before.
 */
export const REFERRAL_OUTBOX_KINDS: readonly OutboundReferralKind[] = OUTBOUND_KINDS.filter(
  // Block 3: an XDIS18 udskrivningsrapport is likewise shown only in its own outbox.
  (kind) => kind !== 'GGOP' && kind !== 'XDIS18' && kind !== 'XDIS21',
);

export type OutboxRow = {
  id: string;
  exchange_id: string;
  envelope_identifier: string;
  letter_identifier: string;
  receiver_ean: string;
  receiver_organisation_name: string | null;
  status: string;
  kind: OutboundReferralKind;
  refuse_code: string | null;
  created_at: string;
  companions: CompanionPointer[];
  /** T1.3: an epikrise copy to another receiver points at the original row. */
  copy_of_id: string | null;
};

/** C12: the effective receipt for one attachment (XBIN02 companion). */
export type CompanionReceipt = {
  referralId: string;
  exchangeId: string;
  status: 'xctl01' | 'xctl02' | 'xctl03';
  receivedAt: string | null;
  refuseCode: string | null;
  refuseText: string | null;
  /** Every receipt for the attachment, newest first. Missing before 17.10.2026 (v13). */
  history?: ReceiptHistoryItem[];
};

type SyncResult = {
  refuseTexts: { referralId: string; refuseText: string | null }[];
  /** Missing from communicate-ctl versions before 17.10.2026. */
  companionReceipts?: CompanionReceipt[];
  /** Missing from communicate-ctl versions before 16.10.2026. */
  receiptTimes?: { referralId: string; receivedAt: string }[];
  /** Every receipt per row, newest first. Missing before 17.10.2026 (receipt history). */
  receiptHistory?: { referralId: string; receipts: ReceiptHistoryItem[] }[];
  syncFailed: boolean;
};

export type OutboxData = {
  rows: OutboxRow[];
  refuseTextById: Record<string, string>;
  /** When the effective receipt was received or imported in Communicate (ISO). */
  receiptReceivedAtById: Record<string, string>;
  /** C12: receipt per attachment, keyed by the companion's exchange id. */
  companionReceiptByExchangeId: Record<string, CompanionReceipt>;
  /** Every receipt per row, newest first; the first is the current status. */
  receiptHistoryById: Record<string, ReceiptHistoryItem[]>;
  syncFailed: boolean;
};

/**
 * Sent referrals for one patient with their receipt status. The table is read
 * first so the list can render; communicate-ctl then syncs receipts in the
 * background and the hook merges status, refusal texts and receipt times.
 */
export function useReferralOutbox(clinicId: string, patientId: string, revision = 0, enabled = true) {
  const queryClient = useQueryClient();
  const syncGeneration = useRef(0);
  const queryKey = ['hospital-referral-outbox', clinicId, patientId, revision] as const;
  return useQuery({
    queryKey,
    queryFn: () =>
      loadOutbox(clinicId, patientId, {
        previous: queryClient.getQueryData<OutboxData>(queryKey),
        isCurrent: (generation) => syncGeneration.current === generation,
        onSynced: (data) => {
          queryClient.setQueryData<OutboxData>(queryKey, data);
        },
        startGeneration: () => ++syncGeneration.current,
      }),
    enabled,
  });
}

async function loadOutbox(
  clinicId: string,
  patientId: string,
  options: {
    previous: OutboxData | undefined;
    isCurrent: (generation: number) => boolean;
    onSynced: (data: OutboxData) => void;
    startGeneration: () => number;
  },
): Promise<OutboxData> {
  const generation = options.startGeneration();
  const rows = await readOutboundReferralRows(clinicId, patientId);
  const immediate = options.previous
    ? { ...options.previous, rows }
    : outboxFromRows(rows, { refuseTexts: [], syncFailed: false });

  void (async () => {
    let sync: SyncResult = { refuseTexts: [], syncFailed: false };
    try {
      sync = await callFunction<
        { action: 'sync-referrals'; clinicId: string; patientId: string },
        SyncResult
      >('communicate-ctl', {
        action: 'sync-referrals',
        clinicId,
        patientId,
      });
    } catch {
      sync = { refuseTexts: [], syncFailed: true };
    }
    if (!options.isCurrent(generation)) return;
    const refreshed = await readOutboundReferralRows(clinicId, patientId);
    if (!options.isCurrent(generation)) return;
    options.onSynced(outboxFromRows(refreshed, sync));
  })();

  return immediate;
}

async function readOutboundReferralRows(clinicId: string, patientId: string): Promise<OutboxRow[]> {
  const { data, error } = await supabase
    .from('clinic_communicate_outbound_referrals')
    .select(
      'id, exchange_id, envelope_identifier, letter_identifier, receiver_ean, receiver_organisation_name, status, kind, refuse_code, created_at, companions, copy_of_id',
    )
    .eq('clinic_id', clinicId)
    .eq('patient_id', patientId)
    .order('created_at', { ascending: false });
  if (error) throw new Error(error.message);
  return (data ?? []).map((row) => ({
    ...row,
    kind: readKind(row.kind),
    companions: readCompanions(row.companions),
  }));
}

function outboxFromRows(rows: OutboxRow[], sync: SyncResult): OutboxData {
  const refuseTextById: Record<string, string> = {};
  for (const note of sync.refuseTexts) {
    if (note.refuseText) refuseTextById[note.referralId] = note.refuseText;
  }
  const receiptHistoryById: Record<string, ReceiptHistoryItem[]> = {};
  for (const entry of sync.receiptHistory ?? []) {
    if (entry.referralId && Array.isArray(entry.receipts)) {
      receiptHistoryById[entry.referralId] = entry.receipts;
    }
  }
  const companionReceiptByExchangeId: Record<string, CompanionReceipt> = {};
  for (const receipt of sync.companionReceipts ?? []) {
    companionReceiptByExchangeId[receipt.exchangeId] = receipt;
  }
  return {
    rows,
    syncFailed: sync.syncFailed,
    refuseTextById,
    receiptReceivedAtById: receiptTimesById(sync.receiptTimes),
    companionReceiptByExchangeId,
    receiptHistoryById,
  };
}

function readKind(value: unknown): OutboundReferralKind {
  return OUTBOUND_KINDS.find((kind) => kind === value) ?? 'XREF01';
}

function readCompanions(value: unknown): CompanionPointer[] {
  if (!Array.isArray(value)) return [];
  const companions: CompanionPointer[] = [];
  for (const item of value) {
    if (!item || typeof item !== 'object') continue;
    const exchangeId = 'exchangeId' in item && typeof item.exchangeId === 'string' ? item.exchangeId : '';
    const fileName = 'fileName' in item && typeof item.fileName === 'string' ? item.fileName : '';
    if (!exchangeId || !fileName) continue;
    companions.push({ exchangeId, fileName });
  }
  return companions;
}

